import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { parseIcd10Codes } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { diagnoses } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ICD10 } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/ICD10'
import { ConditionInfo } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { getHealthPlanICD10s } from '../../../../data/healthPlan/healthPlanSelectors'
import { ICD10s } from '../../../../data/healthPlanReducer'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { GetTransfersResponse } from '../../../../payments/data/transfersApi'

const getPreviousSessionICD10 = (
  icd10Type: string,
  icd10Codes: ICD10s | undefined,
  previousSessionTransfer: { icd10_primary_diagnosis?: string; icd10_secondary_diagnosis?: string },
) => {
  if (!icd10Codes || !Array.isArray(icd10Codes.all)) return []

  if (!previousSessionTransfer || !(icd10Type in previousSessionTransfer)) return []

  // Grab the raw codes string (e.g., "F40.9,F13.24")
  const codesString: string =
    previousSessionTransfer && typeof previousSessionTransfer === 'object' && icd10Type in previousSessionTransfer
      ? (previousSessionTransfer[icd10Type as keyof typeof previousSessionTransfer] as string)
      : ''

  // Let parseIcd10Codes handle splitting and validating
  return parseIcd10Codes(codesString, icd10Codes)
}

const getPreviousSessionDiagnosis = (previousSessionTransfer?: { diagnosis: string }): string[] | undefined => {
  const lastTransfer = previousSessionTransfer
  const lastDiagnoses = lastTransfer && 'diagnosis' in lastTransfer ? lastTransfer.diagnosis?.split(',') : null
  return lastDiagnoses
    ?.map(
      (lastDiagnosis: string) =>
        diagnoses?.find((possibleDiagnosis) => lastDiagnosis === possibleDiagnosis.value)?.label,
    )
    .filter((diagnosisLabel): diagnosisLabel is string => !!diagnosisLabel)
}

const getPreviousSessionConditions = (
  conditionType: string,
  previousSessionTransfer?: { condition_info?: ConditionInfo },
  conditionsList?: { label: string; value: string }[],
) => {
  const lastTransfer = previousSessionTransfer
  const lastDiagnoses = lastTransfer?.condition_info?.[conditionType as keyof ConditionInfo]?.split(',')
  return lastDiagnoses
    ?.map(
      (lastDiagnosis: string) =>
        conditionsList?.find(
          (possibleDiagnosis: { label: string; value: string }) => lastDiagnosis === possibleDiagnosis.value,
        )?.label,
    )
    .filter((diagnosisLabel): diagnosisLabel is string => !!diagnosisLabel)
}

export const useInitialEndOfSessionValues = (
  useEnhancedDiagnosisCapture: boolean,
  nextSessionUsesHealthPlan: boolean,
  previousCharge: GetTransfersResponse | undefined,
  selectedEpisode?: Episode,
  allAppointments?: Appointment[],
  pastPayments?: any[],
  conditionsList?: { label: string; value: string }[],
  dateOfBirth?: string,
  isChargeReversed?: boolean,
) => {
  const isEngageDAProvider = useIsEngageDAProvider()
  const icd10Codes = useSelector(getHealthPlanICD10s)

  const previousSessionTransfer = useMemo(() => {
    if (isEngageDAProvider && isChargeReversed) {
      return previousCharge?.data?.[0]
    }

    const pastEpisodeAppointments =
      (selectedEpisode && allAppointments?.filter((appt: any) => appt.episodeId === selectedEpisode?.id)) ?? []
    const episodeTransfers = pastPayments?.filter((pastPayment: any) =>
      pastEpisodeAppointments.map((appointment: any) => appointment.appointmentId).includes(pastPayment.appointment_id),
    )
    const sortedEpisodeTransfers = episodeTransfers?.sort(
      (a: any, b: any) =>
        (pastEpisodeAppointments.find((appointment) => appointment.appointmentId === b.appointment_id)?.sessionNumber ??
          0) -
        (pastEpisodeAppointments.find((appointment) => appointment.appointmentId === a.appointment_id)?.sessionNumber ??
          0),
    )
    return sortedEpisodeTransfers ? sortedEpisodeTransfers[0] : null
  }, [allAppointments, pastPayments, selectedEpisode, isChargeReversed, previousCharge, isEngageDAProvider])

  const initialValues: {
    primary_condition?: string[] | undefined
    other_primary_description?: string
    secondary_condition?: string[] | undefined
    other_secondary_description?: string
    primary_condition_severity?: string
    underassessment_verification?: boolean
    diagnosis?: [{ label: string; value: string }] | string[]
    icd10_primary_diagnosis?: ICD10[]
    icd10_secondary_diagnosis?: ICD10[]
    attendance?: string
    reference_number?: string | null
    is_last_session?: boolean
    episode_state?: string
    patient_date_of_birth?: string
    session_duration?: string
  } = {}

  if (isEngageDAProvider) {
    if (!isChargeReversed || !previousSessionTransfer) {
      return {}
    } else {
      initialValues.attendance = previousSessionTransfer.attendance
      initialValues.reference_number = previousSessionTransfer.reference_number

      const isLastSession = previousSessionTransfer.last_session || previousSessionTransfer.last_session_terminated
      initialValues.is_last_session = isLastSession

      if (isLastSession && previousSessionTransfer.episode_state) {
        initialValues.episode_state = previousSessionTransfer.episode_state
      }

      initialValues.session_duration = previousSessionTransfer.session_duration

      if (dateOfBirth) {
        initialValues.patient_date_of_birth = dateOfBirth
      }
    }
  }

  if (useEnhancedDiagnosisCapture) {
    initialValues.primary_condition = getPreviousSessionConditions(
      'primary_condition',
      previousSessionTransfer,
      conditionsList,
    )
    initialValues.other_primary_description = previousSessionTransfer?.condition_info?.other_primary_description
    initialValues.secondary_condition = getPreviousSessionConditions(
      'secondary_condition',
      previousSessionTransfer,
      conditionsList,
    )
    initialValues.other_secondary_description = previousSessionTransfer?.condition_info?.other_secondary_description
    initialValues.primary_condition_severity = previousSessionTransfer?.condition_info?.primary_condition_severity
    initialValues.underassessment_verification = previousSessionTransfer?.condition_info?.underassessment_verification
  } else {
    initialValues.diagnosis = getPreviousSessionDiagnosis(previousSessionTransfer)
  }

  if (nextSessionUsesHealthPlan) {
    initialValues.icd10_primary_diagnosis = getPreviousSessionICD10(
      'icd10_primary_diagnosis',
      icd10Codes,
      previousSessionTransfer,
    )
    initialValues.icd10_secondary_diagnosis = getPreviousSessionICD10(
      'icd10_secondary_diagnosis',
      icd10Codes,
      previousSessionTransfer,
    )
  }

  return initialValues
}
